import { useSession } from "next-auth/react";
import { ReactNode, useState } from "react";

import { FileProps } from "@/types";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";

type DownloadLinkProps = {
  file: FileProps;
  children: ReactNode;
  target?: "_blank" | "_self";
};
export default function DownloadLink({ file, children, target = "_self" }: DownloadLinkProps) {
  const { data: session } = useSession();
  const [isDisabled, setIsDisabled] = useState(false);

  // Early return if file is not available
  if (!file.file_size) {
    return <span>-</span>;
  }

  const isAuthenticated = !!session;
  // @ts-expect-error - TODO: add user permissions to the session
  const permissions = session?.permissions || [];

  const handleClick = () => {
    // Disable the link after it's clicked
    setIsDisabled(true);

    // Re-enable the link after a delay
    setTimeout(() => {
      setIsDisabled(false);
    }, 3000);
  };

  // Define rel attribute only if the target is "_blank"
  const rel = target === "_blank" ? "noopener noreferrer" : undefined;

  // Check if the file can be downloaded based on the permission logic
  const { canDownload, instruction } = (() => {
    switch (file.permission) {
      case 1:
        // Permission 1: Public download available
        return {
          canDownload: true,
          instruction: "", // No instruction needed for public download
        };
      case 2:
        // Permission 2: Requires authentication
        return {
          canDownload: isAuthenticated,
          instruction: isAuthenticated ? "" : "Please sign in to download this file",
        };
      case 3:
        // Permission 3: Requires authentication and special LASI data access
        return {
          canDownload: isAuthenticated && permissions.includes(3),
          instruction: isAuthenticated
            ? permissions.includes(3)
              ? ""
              : "Please request LASI data access to download this file"
            : "Please sign in to download this file",
        };
      case 4:
        // Permission 4: Requires authentication and special LASI-DAD data access
        return {
          canDownload: isAuthenticated && permissions.includes(4),
          instruction: isAuthenticated
            ? permissions.includes(4)
              ? ""
              : "Please request LASI-DAD data access to download this file"
            : "Please sign in to download this file",
        };
      case 5:
        // Permission 5: Requires authentication and special RTI-COVID data access
        return {
          canDownload: isAuthenticated && permissions.includes(5),
          instruction: isAuthenticated
            ? permissions.includes(5)
              ? ""
              : "Please request RTI-COVID data access to download this file"
            : "Please sign in to download this file",
        };
      default:
        return {
          canDownload: false,
          instruction: "You don't have permission to download this file",
        };
    }
  })();

  const popover = (
    <Popover id="popover-basic">
      <Popover.Header as="h3">{children}</Popover.Header>
      <Popover.Body>
        <p className="mb-1">File size: {file.file_size}</p>
        <p className="mb-1">File type: {file.mime_subtype}</p>
        {instruction && <p className="mb-1">{instruction}</p>}
      </Popover.Body>
    </Popover>
  );

  // Return the download link or disabled button based on permission check
  return (
    <OverlayTrigger placement="top" overlay={popover}>
      {canDownload ? (
        <span>
          {isDisabled ? (
            <button className="btn btn-link" disabled>
              <div className="spinner-border spinner-border-sm text-center" role="status">
                <span className="visually-hidden">Downloading...</span>
              </div>
            </button>
          ) : (
            <a
              href={file.url}
              target={target}
              rel={rel}
              className="btn btn-link text-decoration-none g2-text-blue"
              onClick={handleClick}
            >
              {children}
            </a>
          )}
        </span>
      ) : (
        <span style={{ cursor: "not-allowed" }}>
          <button className="btn btn-link text-decoration-none g2-text-blue" disabled>
            {children}
          </button>
        </span>
      )}
    </OverlayTrigger>
  );
}
